import { Injectable, inject } from "@angular/core";
import { Observable, of, throwError, timer } from "rxjs";
import { delay, mergeMap } from "rxjs/operators";
import { RonMonitorDataSource } from "../models/ron-monitor.data-source";
import {
  BoardSegmentFilter,
  ClosingDetail,
  ClosingDetailClient,
  ClosingIcon,
  ClosingPill,
  ClientRole,
  NotaryLane,
  NotaryLaneStatus,
  NotarySegment,
  RonMonitorBoard,
  RonMonitorSummary,
  SlotVariant,
} from "../models/ron-monitor.models";
import { RonMonitorClock } from "./ron-monitor-clock.service";

const SLOT_START_HOUR = 8;
const SLOT_MINUTES = 20;
const SLOTS_PER_DAY = 46;
const SESSION_MINUTES = SLOT_MINUTES;
const OVER_OFFSET_MINUTES = SLOT_MINUTES;
const STUCK_THRESHOLD_MINUTES = 5;
const OPEN_SLOT_RATE = 0.15;
const CANCEL_RATE = 0.05;
const PRE_SIGN_RATE = 0.3;
const EXCEPTION_RATE = 0.1;
const MS_PER_DAY = 86_400_000;
const MIN_LATENCY_MS = 150;

export const MAX_LATENCY_MS = 400;

type DayKind = "past" | "today" | "future";

type ExceptionKind = "stuck" | "delayed" | "over";

type DelaySource = "borrower" | "notary";

interface RosterEntry {
  notaryId: string;
  name: string;
  segment: NotarySegment;
}

interface SeedException {
  kind: ExceptionKind;
  source: DelaySource;
  startOffsetMinutes: number;
  resolveAfterMinutes: number | null;
}

interface SeedClosing {
  packageId: number;
  notaryId: string | null;
  slotIndex: number;
  loanNumber: string;
  orderNumber: string;
  signingId: string;
  canceled: boolean;
  hasPreSign: boolean;
  exception: SeedException | null;
  clientIndex: number;
  hasSecondClient: boolean;
  secondClientRole: ClientRole;
  addressIndex: number;
  completionOffsetMinutes: number;
}

interface SeedDay {
  dateKey: string;
  closings: SeedClosing[];
}

interface EvolvedState {
  variant: SlotVariant;
  delayMinutes: number | null;
  icon: ClosingIcon | null;
}

const ROSTER: readonly RosterEntry[] = [
  { notaryId: "sarah", name: "S. Martinez", segment: "TSA" },
  { notaryId: "james", name: "J. Kim", segment: "TSA" },
  { notaryId: "mike", name: "M. Thompson", segment: "PA" },
  { notaryId: "cbrown", name: "C. Brown", segment: "TSA" },
  { notaryId: "lwilson", name: "L. Wilson", segment: "TSA" },
  { notaryId: "rchang", name: "R. Chang", segment: "PA" },
  { notaryId: "alopez", name: "A. Lopez", segment: "TSA" },
  { notaryId: "dpatel", name: "D. Patel", segment: "TSA" },
  { notaryId: "tnguyen", name: "T. Nguyen", segment: "PA" },
  { notaryId: "mrodriguez", name: "M. Rodriguez", segment: "PA" },
  { notaryId: "bwallace", name: "B. Wallace", segment: "TSA" },
];

const CLIENT_FAMILIES: readonly { first: string[]; last: string }[] = [
  { first: ["Linda", "Craig"], last: "Hoffman" },
  { first: ["Diane", "Paul"], last: "Kowalski" },
  { first: ["Franklin", "Sheila"], last: "Morse" },
  { first: ["Patricia", "Kevin"], last: "Nguyen" },
  { first: ["Robert", "Elena"], last: "Fernandez" },
  { first: ["Anthony", "Carmen"], last: "Ruiz" },
  { first: ["Thomas", "Janet"], last: "Ellison" },
  { first: ["Maria", "Sam"], last: "Vasquez" },
  { first: ["David", "Lisa"], last: "Chen" },
  { first: ["Brianna", "Tyler"], last: "Foster" },
  { first: ["Christopher", "Amanda"], last: "Lane" },
  { first: ["Derek", "Paula"], last: "Holloway" },
  { first: ["Victor", "Rosa"], last: "Castillo" },
  { first: ["Jason", "Lisa"], last: "Merritt" },
  { first: ["Grace", "Owen"], last: "Whitfield" },
  { first: ["Priya", "Arun"], last: "Harrington" },
];

const ADDRESSES: readonly string[] = [
  "2791 Blue Heron Blvd, Round Rock, TX 78665",
  "1140 Cypress Ridge Ln, Pflugerville, TX 78660",
  "8815 Wandering Creek Dr, Austin, TX 78748",
  "412 Silver Maple Ct, Cedar Park, TX 78613",
  "6620 Prairie Vista Way, Georgetown, TX 78628",
  "3308 Kestrel Hollow, Leander, TX 78641",
  "77 Winterberry Path, Buda, TX 78610",
  "9051 Sagebrush Terrace, Kyle, TX 78640",
];

function hashSeed(text: string): number {
  let hash = 2166136261;
  for (let index = 0; index < text.length; index++) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function createRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function dayOrdinal(dateKey: string): number {
  const [year, month, day] = dateKey.split("-").map(Number);
  return Math.floor(Date.UTC(year, month - 1, day) / MS_PER_DAY);
}

function toDateKey(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function pickInt(random: () => number, min: number, max: number): number {
  return min + Math.floor(random() * (max - min + 1));
}

@Injectable()
export class MockRonMonitorDataSource extends RonMonitorDataSource {
  private readonly clock = inject(RonMonitorClock);
  private readonly days = new Map<string, SeedDay>();
  private readonly packageIndex = new Map<number, string>();
  private latencyTick = 0;

  getBoard(
    date: Date,
    segment: BoardSegmentFilter
  ): Observable<RonMonitorBoard> {
    const now = this.clock.now();
    const day = this.day(date);
    const dayKind = this.dayKind(day.dateKey, now);

    const closings = day.closings
      .filter((seed) => this.matchesSegment(seed.notaryId, segment))
      .map((seed) => this.toPill(seed, date, now, dayKind));

    const lanes = ROSTER.filter((entry) =>
      this.matchesSegment(entry.notaryId, segment)
    ).map((entry) => this.toLane(entry, closings, dayKind));

    const board: RonMonitorBoard = {
      generatedAt: now.toISOString(),
      summary: this.toSummary(closings),
      lanes,
      closings,
    };

    return of(board).pipe(delay(this.nextLatency()));
  }

  getDetail(packageId: number): Observable<ClosingDetail> {
    const dateKey = this.packageIndex.get(packageId);
    const day = dateKey ? this.days.get(dateKey) : undefined;
    const seed = day?.closings.find((item) => item.packageId === packageId);

    if (!day || !seed) {
      return timer(this.nextLatency()).pipe(
        mergeMap(() =>
          throwError(() => new Error(`Unknown closing ${packageId}`))
        )
      );
    }

    const now = this.clock.now();
    const date = this.fromDateKey(day.dateKey);
    const dayKind = this.dayKind(day.dateKey, now);
    const slotStart = this.slotStart(date, seed.slotIndex);
    const state = this.evolve(seed, slotStart, now, dayKind);

    return of(this.toDetail(seed, slotStart, state)).pipe(
      delay(this.nextLatency())
    );
  }

  private day(date: Date): SeedDay {
    const dateKey = toDateKey(date);
    const cached = this.days.get(dateKey);
    if (cached) {
      return cached;
    }

    const generated = this.generateDay(dateKey);
    this.days.set(dateKey, generated);
    generated.closings.forEach((seed) =>
      this.packageIndex.set(seed.packageId, dateKey)
    );
    return generated;
  }

  private generateDay(dateKey: string): SeedDay {
    const seedValue = hashSeed(dateKey);
    const random = createRandom(seedValue);
    const packageIdBase = 500_000 + dayOrdinal(dateKey) * 1_000;
    const loanBase = 3_000_000_000 + (seedValue % 500_000_000);
    const compactDate = dateKey.replace(/-/g, "");
    const closings: SeedClosing[] = [];

    let sequence = 0;
    const makeClosing = (
      notaryId: string | null,
      slotIndex: number
    ): SeedClosing => {
      sequence++;
      const canceled = random() < CANCEL_RATE;
      return {
        packageId: packageIdBase + sequence,
        notaryId,
        slotIndex,
        loanNumber: `${loanBase + sequence * 37}`.slice(0, 10),
        orderNumber: `RON-${compactDate}-${`${sequence}`.padStart(3, "0")}`,
        signingId: `SGN-${compactDate}-${`${sequence}`.padStart(3, "0")}`,
        canceled,
        hasPreSign: random() < PRE_SIGN_RATE,
        exception:
          !canceled && random() < EXCEPTION_RATE
            ? this.makeException(random)
            : null,
        clientIndex: pickInt(random, 0, CLIENT_FAMILIES.length - 1),
        hasSecondClient: random() < 0.75,
        secondClientRole: random() < 0.85 ? "Co-borrower" : "NBTH",
        addressIndex: pickInt(random, 0, ADDRESSES.length - 1),
        completionOffsetMinutes: pickInt(random, 14, 19),
      };
    };

    ROSTER.forEach((entry) => {
      for (let slotIndex = 0; slotIndex < SLOTS_PER_DAY; slotIndex++) {
        if (random() < OPEN_SLOT_RATE) {
          continue;
        }
        closings.push(makeClosing(entry.notaryId, slotIndex));
      }
    });

    const unassignedSlots = [pickInt(random, 12, 20), pickInt(random, 21, 30)];
    unassignedSlots.forEach((slotIndex) =>
      closings.push(makeClosing(null, slotIndex))
    );

    return { dateKey, closings };
  }

  private makeException(random: () => number): SeedException {
    const roll = random();
    const kind: ExceptionKind =
      roll < 0.35 ? "stuck" : roll < 0.8 ? "delayed" : "over";
    const source: DelaySource =
      kind === "over" ? "notary" : kind === "stuck" ? "borrower" : random() < 0.6 ? "borrower" : "notary";

    return {
      kind,
      source,
      startOffsetMinutes: kind === "over" ? OVER_OFFSET_MINUTES : 0,
      resolveAfterMinutes: random() < 0.6 ? pickInt(random, 8, 22) : null,
    };
  }

  private toPill(
    seed: SeedClosing,
    date: Date,
    now: Date,
    dayKind: DayKind
  ): ClosingPill {
    const slotStart = this.slotStart(date, seed.slotIndex);
    const state = this.evolve(seed, slotStart, now, dayKind);

    return {
      packageId: seed.packageId,
      notaryId: seed.notaryId,
      scheduledDateTime: slotStart.toISOString(),
      variant: state.variant,
      delayMinutes: state.delayMinutes,
      icon: state.icon,
      loanNumber: seed.loanNumber,
      orderNumber: seed.orderNumber,
    };
  }

  private evolve(
    seed: SeedClosing,
    slotStart: Date,
    now: Date,
    dayKind: DayKind
  ): EvolvedState {
    if (seed.canceled) {
      return { variant: "canceled", delayMinutes: null, icon: null };
    }

    if (dayKind === "past") {
      return { variant: "completed", delayMinutes: null, icon: null };
    }

    if (dayKind === "future") {
      return { variant: "scheduled", delayMinutes: null, icon: null };
    }

    const offsetMinutes = (now.getTime() - slotStart.getTime()) / 60_000;
    const exception = seed.exception;

    if (exception && offsetMinutes >= exception.startOffsetMinutes) {
      const elapsed = Math.floor(offsetMinutes - exception.startOffsetMinutes);
      const unresolved =
        exception.resolveAfterMinutes === null ||
        elapsed <= exception.resolveAfterMinutes;

      if (unresolved) {
        return {
          variant: exception.kind,
          delayMinutes: Math.max(1, elapsed),
          icon: this.exceptionIcon(exception),
        };
      }
    }

    if (offsetMinutes < -SLOT_MINUTES) {
      return { variant: "scheduled", delayMinutes: null, icon: null };
    }

    if (offsetMinutes < 0) {
      return {
        variant: seed.hasPreSign ? "pre-sign" : "check-in",
        delayMinutes: null,
        icon: seed.hasPreSign ? "stamp" : "person",
      };
    }

    if (offsetMinutes < SESSION_MINUTES) {
      return { variant: "in-session", delayMinutes: null, icon: "stamp" };
    }

    return { variant: "completed", delayMinutes: null, icon: null };
  }

  private exceptionIcon(exception: SeedException): ClosingIcon {
    if (exception.kind === "over") {
      return "stamp";
    }
    return exception.source === "notary" ? "headphone" : "person";
  }

  private toSummary(closings: ClosingPill[]): RonMonitorSummary {
    const count = (predicate: (pill: ClosingPill) => boolean): number =>
      closings.filter(predicate).length;

    const scheduled = count((pill) => pill.variant !== "canceled");

    return {
      scheduled,
      stuckClients: count((pill) => pill.variant === "stuck"),
      borrowerDelayed: count(
        (pill) => pill.variant === "delayed" && pill.icon === "person"
      ),
      notaryDelayed: count(
        (pill) => pill.variant === "delayed" && pill.icon === "headphone"
      ),
      runningOver: count((pill) => pill.variant === "over"),
      completed: count((pill) => pill.variant === "completed"),
      completedScheduledDenominator: scheduled,
      cancelled: count((pill) => pill.variant === "canceled"),
      openSlots: null,
      stuckThresholdMinutes: STUCK_THRESHOLD_MINUTES,
    };
  }

  private toLane(
    entry: RosterEntry,
    closings: ClosingPill[],
    dayKind: DayKind
  ): NotaryLane {
    const owned = closings.filter((pill) => pill.notaryId === entry.notaryId);

    return {
      notaryId: entry.notaryId,
      name: entry.name,
      segment: entry.segment,
      currentStatus: this.toLaneStatus(owned, dayKind),
    };
  }

  private toLaneStatus(
    owned: ClosingPill[],
    dayKind: DayKind
  ): NotaryLaneStatus {
    const has = (variant: SlotVariant): boolean =>
      owned.some((pill) => pill.variant === variant);

    if (has("stuck")) {
      return { label: "Stuck", tagVariant: "error" };
    }
    if (has("delayed") || has("over")) {
      return { label: "Running behind", tagVariant: "warning" };
    }
    if (has("in-session")) {
      return { label: "In session", tagVariant: "info" };
    }
    if (has("check-in") || has("pre-sign")) {
      return { label: "Checking in", tagVariant: "info" };
    }
    if (dayKind === "past") {
      return { label: "Day complete", tagVariant: "success" };
    }
    if (dayKind === "future") {
      return { label: "Scheduled", tagVariant: "slate" };
    }
    return { label: "Waiting", tagVariant: "default" };
  }

  private toDetail(
    seed: SeedClosing,
    slotStart: Date,
    state: EvolvedState
  ): ClosingDetail {
    const family = CLIENT_FAMILIES[seed.clientIndex];
    const phoneBase = 5_550_000 + seed.clientIndex * 137;
    const clients: ClosingDetailClient[] = [
      {
        name: `${family.first[0]} ${family.last}`,
        role: "Borrower",
        currently: this.clientCurrently(state, true),
        phone: `512-${`${phoneBase}`.slice(0, 3)}-${`${phoneBase}`.slice(3, 7)}`,
      },
    ];

    if (seed.hasSecondClient) {
      clients.push({
        name: `${family.first[1]} ${family.last}`,
        role: seed.secondClientRole,
        currently: this.clientCurrently(state, false),
        phone: `512-${`${phoneBase + 1}`.slice(0, 3)}-${`${phoneBase + 1}`.slice(
          3,
          7
        )}`,
      });
    }

    const notaryName = seed.notaryId
      ? ROSTER.find((entry) => entry.notaryId === seed.notaryId)?.name ??
        "Unassigned"
      : "Unassigned";

    const completedAt =
      state.variant === "completed"
        ? new Date(
            slotStart.getTime() + seed.completionOffsetMinutes * 60_000
          ).toISOString()
        : null;

    return {
      packageId: seed.packageId,
      notary: {
        name: notaryName,
        status: this.notaryStatus(state),
        currently: this.notaryCurrently(state),
        reason: this.notaryReason(state),
        phone: seed.notaryId ? "313-555-0142" : null,
      },
      clients,
      closing: {
        loanNumber: seed.loanNumber,
        signingId: seed.signingId,
        propertyAddress: ADDRESSES[seed.addressIndex],
        scheduledAt: slotStart.toISOString(),
        completedAt,
        rescheduledTo: null,
      },
    };
  }

  private notaryStatus(state: EvolvedState): string {
    switch (state.variant) {
      case "stuck":
        return "Waiting";
      case "delayed":
        return "Delayed";
      case "over":
        return "Running over";
      case "in-session":
        return "In session";
      case "check-in":
      case "pre-sign":
        return "Checking in";
      case "completed":
        return "Complete";
      case "canceled":
        return "Canceled";
      default:
        return "Scheduled";
    }
  }

  private notaryCurrently(state: EvolvedState): string {
    switch (state.variant) {
      case "stuck":
        return "Available — session not started";
      case "delayed":
        return "Waiting on client to join";
      case "over":
        return "Notarizing documents";
      case "in-session":
        return "Session in progress";
      case "check-in":
        return "Verifying client identity";
      case "pre-sign":
        return "Reviewing pre-signed documents";
      case "completed":
        return "Session complete — documents submitted";
      case "canceled":
        return "Session closed";
      default:
        return "Not yet started";
    }
  }

  private notaryReason(state: EvolvedState): string | null {
    const minutes = state.delayMinutes;
    switch (state.variant) {
      case "stuck":
        return `Client check-in incomplete — borrower ID verification failing (${minutes} min)`;
      case "delayed":
        return state.icon === "headphone"
          ? `Notary joined late — ${minutes} min behind`
          : `Client has not joined — ${minutes} min past start`;
      case "over":
        return `Session running ${minutes} min past the scheduled window`;
      case "canceled":
        return "Closing canceled before session start";
      default:
        return null;
    }
  }

  private clientCurrently(state: EvolvedState, isPrimary: boolean): string {
    const minutes = state.delayMinutes;
    switch (state.variant) {
      case "stuck":
        return isPrimary
          ? `Stuck at ID verification — ${minutes} min`
          : "Waiting — co-signer ready";
      case "delayed":
        return `Waiting room — ${minutes} min`;
      case "over":
        return "Connected — notarizing documents";
      case "in-session":
        return "Connected — signing documents";
      case "check-in":
        return "Checking in — identity verification pending";
      case "pre-sign":
        return "Reviewing documents before session";
      case "completed":
        return "Session complete — documents submitted";
      case "canceled":
        return "Not connected — session closed";
      default:
        return "Not yet connected";
    }
  }

  private matchesSegment(
    notaryId: string | null,
    segment: BoardSegmentFilter
  ): boolean {
    if (segment === "all") {
      return true;
    }
    if (notaryId === null) {
      return false;
    }
    const entry = ROSTER.find((item) => item.notaryId === notaryId);
    if (!entry) {
      return false;
    }
    return segment === "tsa" ? entry.segment === "TSA" : entry.segment === "PA";
  }

  private slotStart(date: Date, slotIndex: number): Date {
    const start = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      SLOT_START_HOUR,
      0,
      0,
      0
    );
    start.setMinutes(start.getMinutes() + slotIndex * SLOT_MINUTES);
    return start;
  }

  private dayKind(dateKey: string, now: Date): DayKind {
    const todayKey = toDateKey(now);
    if (dateKey === todayKey) {
      return "today";
    }
    return dateKey < todayKey ? "past" : "future";
  }

  private fromDateKey(dateKey: string): Date {
    const [year, month, day] = dateKey.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  private nextLatency(): number {
    this.latencyTick = (this.latencyTick + 7) % 11;
    const span = MAX_LATENCY_MS - MIN_LATENCY_MS;
    return MIN_LATENCY_MS + Math.round((this.latencyTick / 10) * span);
  }
}
